'use client';

import { createContext, useContext, useState, useCallback, type ReactNode } from 'react';
import type {
  FindSessionEntry,
  FindSessionTab,
} from '@/components/landing/find-session-modal/FindSessionModal.types';

interface FindSessionContextValue {
  open: boolean;
  initialTab: FindSessionTab;
  entry: FindSessionEntry;
  openModal: (tab?: FindSessionTab, entry?: FindSessionEntry) => void;
  closeModal: () => void;
}

const FindSessionContext = createContext<FindSessionContextValue | null>(null);

export function FindSessionProvider({ children }: { children: ReactNode }) {
  const [open, setOpen] = useState(false);
  const [initialTab, setInitialTab] = useState<FindSessionTab>('vehicle');
  const [entry, setEntry] = useState<FindSessionEntry>('payByPlate');

  const openModal = useCallback(
    (tab: FindSessionTab = 'vehicle', nextEntry: FindSessionEntry = 'payByPlate') => {
      setInitialTab(tab);
      setEntry(nextEntry);
      setOpen(true);
    },
    []
  );

  const closeModal = useCallback(() => {
    setOpen(false);
  }, []);

  return (
    <FindSessionContext.Provider value={{ open, initialTab, entry, openModal, closeModal }}>
      {children}
    </FindSessionContext.Provider>
  );
}

export function useFindSessionContext() {
  const ctx = useContext(FindSessionContext);
  if (!ctx) throw new Error('useFindSessionContext must be used within FindSessionProvider');
  return ctx;
}

/**
 * Like {@link useFindSessionContext} but returns null outside a provider instead of throwing.
 *
 * ## Invariants:
 * - Only for surfaces that render under BOTH layouts (the site header, PB-9032): the `(app)` layout
 *   mounts no `FindSessionProvider`, so a throwing accessor would crash `/myaccount`. A null
 *   result means "no find-session pop-up here", and the caller hides the item instead.
 */
export function useOptionalFindSessionContext() {
  return useContext(FindSessionContext);
}
